import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
} from '@mui/material';
import { FolderOpen, Save, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdTextField from '@/components/ui/CpdTextField';
import CpdDropdown from '@/components/ui/CpdDropdown';
import { FacilityGroupsApi } from '@/api/clients/facility-groups/facility-groups.api';
import { useGetFacilityGroups } from '@/api/hooks/facility-groups/useGetFacilityGroups';
import useFormValidation from '@/hooks/useFormValidation';

const FacilityGroupFormDialog = ({
  open,
  onClose,
  onSuccess,
  organizationId,
  facilityGroup = null,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!facilityGroup;
  const [loading, setLoading] = useState(false);
  const {
    facilityGroups: availableParents,
  } = useGetFacilityGroups(organizationId, { enabled: open });
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: '',
  });

  useEffect(() => {
    if (open) {
      clearAllErrors();
      if (facilityGroup) {
        setFormData({
          name: facilityGroup.name || '',
          description: facilityGroup.description || '',
          parent_id: facilityGroup.parent_id || '',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          parent_id: '',
        });
      }
    }
  }, [facilityGroup, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleParentChange = (e) => {
    setFormData((prev) => ({ ...prev, parent_id: e.target.value }));
  };

  const handleSave = async () => {
    clearAllErrors();
    setLoading(true);
    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        parent_id: formData.parent_id || null,
      };

      if (isEditMode) {
        await FacilityGroupsApi.updateFacilityGroup(
          organizationId,
          facilityGroup.id,
          dataToSave,
        );
        onSuccess?.();
      } else {
        const response = await FacilityGroupsApi.createFacilityGroup(
          organizationId,
          dataToSave,
        );
        const newGroupId = response.data?.id;
        onSuccess?.(newGroupId);
      }
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const filteredParents = useMemo(() => {
    if (!isEditMode) return availableParents;
    return availableParents.filter((p) => p.id !== facilityGroup.id);
  }, [availableParents, facilityGroup, isEditMode]);

  const isFormDisabled = loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderOpen color='primary' />
          {isEditMode
            ? t('facilityGroups.editTitle')
            : t('facilityGroups.createTitle')}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column' }}>
          <FormErrorSummary errors={generalErrors} />

          <CpdTextField
            name='name'
            label={t('facilityGroups.form.name')}
            value={formData.name}
            onChange={handleChange}
            required
            disabled={isFormDisabled}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            inputProps={{ maxLength: 255 }}
          />

          <CpdTextField
            name='description'
            label={t('facilityGroups.form.description')}
            value={formData.description}
            onChange={handleChange}
            disabled={isFormDisabled}
            error={!!fieldErrors.description}
            helperText={fieldErrors.description}
            multiline
            rows={3}
          />

          <CpdDropdown
            label={t('facilityGroups.form.parentGroup')}
            options={filteredParents}
            value={formData.parent_id}
            onChange={handleParentChange}
            disabled={isFormDisabled}
            error={!!fieldErrors.parent_id}
            helperText={fieldErrors.parent_id}
            placeholder={t('facilityGroups.form.noParent')}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />}>
          {t('common.buttons.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleSave}
          loading={loading}
          disabled={!formData.name.trim()}
          startIcon={<Save />}>
          {isEditMode ? t('common.buttons.save') : t('common.buttons.create')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default FacilityGroupFormDialog;
